import React, { useEffect, useState, useRef } from 'react';
import { Modal } from '@mui/material';
import { Button } from '../../components/ui';
import { SearchIcon } from '@heroicons/react/solid';
import { InlineLoader, PrimaryLoader } from '../../components/Loader';
import { useIntersectionObserver } from 'usehooks-ts';

const UserManagementModal = ({ 
  open, 
  onClose, 
  geofenceName, 
  availableUsers = [], 
  addedUsers = [], 
  onAddUser, 
  onRemoveUser, 
  onSave,
  onAvailableUsersSearch,
  onAddedUsersSearch,
  loadMoreAvailableUsers,
  loadMoreAddedUsers,
  availableUsersSearchQuery = '',
  addedUsersSearchQuery = '',
  isLoading = false,
  isAvailableUsersLoading = false,
  isAddedUsersLoading = false,
  isFetchingAvailableUsers = false,
  isFetchingAddedUsers = false,
  hasMoreAvailableUsers = false,
  hasMoreAddedUsers = false,
  isSubmitting = false
}) => {
  // Create local state for search inputs to prevent input clearing
  const [availableSearchInput, setAvailableSearchInput] = useState('');
  const [addedSearchInput, setAddedSearchInput] = useState('');
  
  // Create refs for scrollable containers
  const availableUsersContainerRef = useRef(null);
  const addedUsersContainerRef = useRef(null);
  
  // Intersection observers for infinite scrolling
  const { 
    ref: availableUsersIntersectionRef, 
    isIntersecting: isAvailableUsersIntersecting 
  } = useIntersectionObserver({
    root: availableUsersContainerRef.current,
    threshold: 0.5,
    rootMargin: '0px',
  });
  
  const { 
    ref: addedUsersIntersectionRef, 
    isIntersecting: isAddedUsersIntersecting 
  } = useIntersectionObserver({
    root: addedUsersContainerRef.current,
    threshold: 0.5,
    rootMargin: '0px',
  });
  
  // Effect to load more available users when scrolling
  useEffect(() => {
    if (
      isAvailableUsersIntersecting && 
      !isAvailableUsersLoading && 
      !isFetchingAvailableUsers && 
      hasMoreAvailableUsers &&
      !isSubmitting
    ) {
      loadMoreAvailableUsers();
    }
  }, [
    isAvailableUsersIntersecting, 
    isAvailableUsersLoading, 
    isFetchingAvailableUsers, 
    hasMoreAvailableUsers,
    loadMoreAvailableUsers,
    isSubmitting
  ]);
  
  // Effect to load more added users when scrolling
  useEffect(() => {
    if (
      isAddedUsersIntersecting && 
      !isAddedUsersLoading && 
      !isFetchingAddedUsers && 
      hasMoreAddedUsers &&
      !isSubmitting
    ) {
      loadMoreAddedUsers();
    }
  }, [
    isAddedUsersIntersecting, 
    isAddedUsersLoading, 
    isFetchingAddedUsers, 
    hasMoreAddedUsers,
    loadMoreAddedUsers,
    isSubmitting
  ]);

  // Reset search terms ONLY when modal opens/closes
  useEffect(() => {
    if (open) {
      setAvailableSearchInput('');
      setAddedSearchInput('');
    }
  }, [open]);
  
  // Update local state and trigger search with debouncing handled by parent
  const handleAvailableSearch = (e) => {
    const value = e.target.value;
    setAvailableSearchInput(value);
    if (onAvailableUsersSearch) onAvailableUsersSearch(value);
  };

  const handleAddedSearch = (e) => {
    const value = e.target.value;
    setAddedSearchInput(value);
    if (onAddedUsersSearch) onAddedUsersSearch(value);
  };

  return (
    <Modal
      open={open}
      onClose={isSubmitting ? null : onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className="w-[80%] bg-white absolute left-1/2 -translate-x-1/2 top-[10%] p-6 rounded-md max-h-[80vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          Manage Users for {geofenceName}
        </h2>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Available Users Column */}
          <div className="border rounded-md p-4">
            <h3 className="text-lg font-semibold mb-3">Available Users</h3>
            
            {/* Search bar for Available Users */}
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="input pl-10 w-full"
                placeholder="Search users..."
                value={availableSearchInput}
                onChange={handleAvailableSearch}
                disabled={isSubmitting}
              />
            </div>
            
            <div 
              ref={availableUsersContainerRef}
              className="max-h-[350px] overflow-y-auto"
            >
              {isAvailableUsersLoading && !isFetchingAvailableUsers ? (
                <div className="flex justify-center items-center py-10">
                  <PrimaryLoader />
                </div>
              ) : (
                <>
                  {availableUsers.map(user => (
                    <div 
                      key={user.company_employee_id}
                      onClick={() => !isSubmitting && onAddUser(user)}
                      className={`p-2 border-b ${isSubmitting ? 'bg-gray-100' : 'hover:bg-gray-100 cursor-pointer'} flex justify-between items-center`}
                    >
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <span className="text-green-500 text-sm">Click to add</span>
                    </div>
                  ))}
                  
                  {/* Intersection observer for infinite scroll */}
                  <div
                    ref={availableUsersIntersectionRef}
                    className="h-10 flex justify-center items-center"
                  >
                    {isFetchingAvailableUsers ? (
                      <InlineLoader />
                    ) : hasMoreAvailableUsers ? (
                      <span className="text-sm text-gray-500">Scroll for more</span>
                    ) : null}
                  </div>
                  
                  {availableUsers.length === 0 && !isAvailableUsersLoading && !isFetchingAvailableUsers && (
                    <p className="text-gray-500 italic text-center py-4">
                      No available users found
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Added Users Column */}
          <div className="border rounded-md p-4">
            <h3 className="text-lg font-semibold mb-3">Added Users</h3>
            
            {/* Search bar for Added Users */}
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="input pl-10 w-full"
                placeholder="Search added users..."
                value={addedSearchInput}
                onChange={handleAddedSearch}
                disabled={isSubmitting}
              />
            </div>
            
            <div 
              ref={addedUsersContainerRef}
              className="max-h-[350px] overflow-y-auto"
            >
              {isAddedUsersLoading && !isFetchingAddedUsers ? (
                <div className="flex justify-center items-center py-10">
                  <PrimaryLoader />
                </div>
              ) : (
                <>
                  {addedUsers.map(user => (
                    <div 
                      key={user.company_employee_id}
                      onClick={() => !isSubmitting && onRemoveUser(user)}
                      className={`p-2 border-b ${isSubmitting ? 'bg-gray-100' : 'hover:bg-gray-100 cursor-pointer'} flex justify-between items-center`}
                    >
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <span className="text-red-500 text-sm">Click to remove</span>
                    </div>
                  ))}
                  
                  {/* Intersection observer for infinite scroll */}
                  <div
                    ref={addedUsersIntersectionRef}
                    className="h-10 flex justify-center items-center"
                  >
                    {isFetchingAddedUsers ? (
                      <InlineLoader />
                    ) : hasMoreAddedUsers ? (
                      <span className="text-sm text-gray-500">Scroll for more</span>
                    ) : null}
                  </div>
                  
                  {addedUsers.length === 0 && !isAddedUsersLoading && !isFetchingAddedUsers && (
                    <p className="text-gray-500 italic text-center py-4">
                      No users added yet
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <Button 
            variant="ghost" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={onSave}
            disabled={isLoading || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <InlineLoader className="text-white mr-2" />
                Saving...
              </>
            ) : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UserManagementModal;
